import React, { useEffect, useMemo, useState } from "react";
import { CalendarDays, Check, ChevronRight, Globe2, Minus, Plus, ShoppingBag, Users } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import SEO from "../SEO/SEO";
import { get, post } from "../../utils/request";
import { notifyCartUpdated } from "../Cart/CartContext";
import "./deal-detail.scss";

const dateValue = (date) => date.toISOString().slice(0, 10);
const addDays = (date, days) => { const next = new Date(date); next.setUTCDate(next.getUTCDate() + days); return next; };
const serviceLabel = (type) => ({ esim: "eSIM", thingsToDo: "Things To Do", airportLounge: "Airport Lounge", flights: "Flights", transfer: "Transfer" }[type] || type);

function initialDates(deal) {
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const configuredStart = deal?.travelValidity?.startDate ? new Date(deal.travelValidity.startDate) : today;
  const configuredEnd = deal?.travelValidity?.endDate ? new Date(deal.travelValidity.endDate) : null;
  const departure = configuredStart > today ? configuredStart : today;
  const duration = Math.max(1, Number(deal?.durationNights || 1));
  let returning = addDays(departure, duration);
  if (configuredEnd && returning > configuredEnd) returning = configuredEnd;
  return { departureDate: dateValue(departure), returnDate: dateValue(returning) };
}

const travellerId = (traveller) => String(traveller?.id || traveller?._id || "");
const travellerCount = (party) => Number(party.adults || 0) + Number(party.children || 0) + Number(party.infants || 0);
const formatTravellerDob = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime())
    ? new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" }).format(date)
    : "Date of birth not provided";
};

export default function DealDetailPage() {
  const { shareSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [party, setParty] = useState({ adults: 1, children: 0, infants: 0 });
  const [trip, setTrip] = useState({ departureDate: "", returnDate: "", originIata: "" });
  const [savedTravellers, setSavedTravellers] = useState([]);
  const [selectedTravellerIds, setSelectedTravellerIds] = useState([]);
  const [travellersLoading, setTravellersLoading] = useState(false);
  const [travellersError, setTravellersError] = useState("");
  const [loungeOptions, setLoungeOptions] = useState({});
  const [selections, setSelections] = useState({});

  useEffect(() => {
    let active = true;
    setLoading(true);
    get(`/bundleDeals/public/share/${encodeURIComponent(shareSlug)}`)
      .then((result) => {
        if (!active) return;
        setDeal(result);
        const nextParty = result?.includedParty || { adults: 1, children: 0, infants: 0 };
        const normalizedParty = { adults: Math.max(1, Number(nextParty.adults || 1)), children: Number(nextParty.children || 0), infants: Number(nextParty.infants || 0) };
        setParty(normalizedParty);
        const dates = initialDates(result);
        setTrip({ ...dates, originIata: result?.originIata || "" });
      })
      .catch(() => active && toast.error("This travel deal is no longer available."))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [shareSlug]);

  useEffect(() => {
    if (!localStorage.getItem("teleport_token")) return;
    let active = true;
    setTravellersLoading(true);
    setTravellersError("");
    get("/passengers/getAll")
      .then((result) => {
        if (!active) return;
        setSavedTravellers(Array.isArray(result) ? result : []);
      })
      .catch(() => {
        if (active) setTravellersError("Unable to load Traveller Information. Please try again.");
      })
      .finally(() => active && setTravellersLoading(false));
    return () => { active = false; };
  }, []);

  const hasFlights = useMemo(
    () => Boolean(deal?.services?.some((service) => service.serviceType === "flights" && service.required !== false)),
    [deal]
  );
  const requiredTravellerCount = travellerCount(party);
  const selectedTravellers = useMemo(() => {
    const selected = new Set(selectedTravellerIds);
    return savedTravellers.filter((traveller) => selected.has(travellerId(traveller)));
  }, [savedTravellers, selectedTravellerIds]);

  const pricing = useMemo(() => {
    if (!deal) return { deal: 0, original: 0 };
    const byCategory = new Map((deal.travellerDealPricing || []).map((row) => [row.category, row]));
    const counts = { adult: party.adults, child: party.children, infant: party.infants };
    return Object.entries(counts).reduce((total, [category, count]) => {
      const row = byCategory.get(category);
      return { deal: total.deal + Number(row?.dealPrice ?? deal.price ?? 0) * count, original: total.original + Number(row?.bundlePrice ?? deal.originalPrice ?? 0) * count };
    }, { deal: 0, original: 0 });
  }, [deal, party]);

  const updateParty = (category, delta) => {
    setParty((current) => {
      const minimum = category === "adults" ? 1 : 0;
      const next = { ...current, [category]: Math.max(minimum, current[category] + delta) };
      if (next.infants > next.adults) next.infants = next.adults;
      setSelectedTravellerIds([]);
      setLoungeOptions({});
      setSelections({});
      return next;
    });
  };

  const toggleTraveller = (traveller) => {
    const id = travellerId(traveller);
    if (!id) return;
    setSelectedTravellerIds((current) => {
      if (current.includes(id)) return current.filter((item) => item !== id);
      if (current.length >= requiredTravellerCount) return current;
      return [...current, id];
    });
  };

  const payload = () => {
    const participantInfos = selectedTravellers.map((traveller) => ({
      ...traveller,
      passengerId: travellerId(traveller),
    }));
    const activitySelections = {};
    (deal.services || []).filter((service) => service.required).forEach((service) => {
      const selected = selections[service.key] || {};
      const optionSelections = [
        ["adult", party.adults], ["child", party.children], ["infant", party.infants],
      ].flatMap(([category, quantity]) => {
        const price = (service.travellerPricing || []).find((row) => row.category === category);
        return quantity > 0 && price ? [{ category, optionIndex: price.optionIndex, providerTicketTypeId: price.providerTicketTypeId || "", quantity }] : [];
      });
      activitySelections[service.key] = {
        ...selected,
        visitDate: selected.visitDate || trip.departureDate,
        participantInfos,
        ...(optionSelections.length ? { optionSelections } : {}),
      };
    });
    return {
      currency: deal.currency,
      countryCode: deal.applicableCountryCode,
      trip: {
        departureDate: trip.departureDate,
        returnDate: trip.returnDate,
        destinationIata: deal.destinationIata || "",
        ...(hasFlights ? { originIata: trip.originIata.toUpperCase(), originCountryCode: deal.applicableCountryCode, cabinCode: deal.flightRules?.cabinCodes?.[0] || "Economy" } : {}),
      },
      party,
      activitySelections,
    };
  };

  const loadLounges = async (service) => {
    try {
      const result = await post(`/bundleDeals/public/${deal._id}/lounge-options`, { ...payload(), serviceKey: service.key });
      setLoungeOptions((current) => ({ ...current, [service.key]: result?.options || [] }));
      if (!result?.options?.length) toast.info("No eligible lounges are currently available for these dates.");
    } catch (error) {
      toast.error(error?.message || "Unable to load lounge choices.");
    }
  };

  const addToCart = async () => {
    if (!localStorage.getItem("teleport_token")) {
      navigate("/login", { state: { from: location.pathname } });
      return;
    }
    if (hasFlights && (!trip.departureDate || !trip.returnDate || trip.returnDate <= trip.departureDate)) return toast.error("Choose a valid departure and return date.");
    if (selectedTravellers.length !== requiredTravellerCount) return toast.error(`Select exactly ${requiredTravellerCount} saved traveller${requiredTravellerCount === 1 ? "" : "s"}.`);
    const incompleteTraveller = selectedTravellers.find((traveller) => !String(traveller.firstName || "").trim() || !String(traveller.lastName || "").trim() || !traveller.dob);
    if (incompleteTraveller) return toast.error("Complete the selected traveller's name and date of birth in Traveller Information.");
    const requiredPoolLounge = deal.services?.find((service) => service.required && service.serviceType === "airportLounge" && service.loungeSelectionMode === "countryPool" && !selections[service.key]?.loungeId);
    if (requiredPoolLounge) return toast.error(`Choose a lounge for ${requiredPoolLounge.title}.`);
    setAdding(true);
    try {
      await post(`/bundleDeals/public/${deal._id}/cart`, payload());
      notifyCartUpdated();
      toast.success("Bundle deal added to your cart.");
      navigate("/cart");
    } catch (error) {
      toast.error(error?.message || "Unable to add this deal to your cart.");
    } finally {
      setAdding(false);
    }
  };

  if (loading) return <><Navbar /><main className="deal-detail-loading">Loading this travel deal…</main><Footer /></>;
  if (!deal) return <><Navbar /><main className="deal-detail-loading"><h1>Deal unavailable</h1><p>This shared deal may have ended or been removed.</p></main><Footer /></>;

  return (
    <><SEO title={`${deal.title} | Teleport Now Deals`} description={deal.subtitle || `Book ${deal.title} on Teleport Now.`} canonicalPath={`/dealsforyou/${shareSlug}`} />
      <Navbar />
      <main className="deal-detail-page">
        <section className="deal-detail-hero">
          <img src={deal.heroImageUrl} alt="" />
          <div className="deal-detail-hero__veil" />
          <div className="deal-detail-hero__copy">
            <p className="teleport-eyebrow">Teleport Now Deals For You</p>
            <h1>{deal.title}</h1>
            <p>{deal.subtitle}</p>
            <div>
              {deal.applicabilityMode !== "global" && deal.applicableCountry && <span><Globe2 /> {deal.applicableCountry}</span>}
              {hasFlights && <span><CalendarDays /> {deal.durationNights} nights</span>}
              <span><Users /> From {deal.currency} {pricing.deal.toFixed(2)}</span>
            </div>
          </div>
        </section>

        <div className="deal-detail-layout">
          <section className="deal-detail-main">
            <article className="deal-card"><p className="teleport-eyebrow">What is included</p><h2>One deal, multiple travel essentials.</h2><div className="deal-services">{deal.services?.map((service) => <div key={service.key}><img src={service.imageUrl || deal.heroImageUrl} alt="" /><div><span>{serviceLabel(service.serviceType)}</span><h3>{service.title}</h3><p>{service.subtitle}</p></div><strong><Check /> Included</strong></div>)}</div></article>

            {hasFlights && <article className="deal-card">
              <p className="teleport-eyebrow">Booking Options</p>
              <h2>Choose your flight dates.</h2>
              <div className="deal-form-grid">
                <label>Departure date<input type="date" value={trip.departureDate} min={deal.travelValidity?.startDate?.slice(0,10)} max={deal.travelValidity?.endDate?.slice(0,10)} onChange={(event) => setTrip((current) => ({ ...current, departureDate: event.target.value }))} /></label>
                <label>Return date<input type="date" value={trip.returnDate} min={trip.departureDate} max={deal.travelValidity?.endDate?.slice(0,10)} onChange={(event) => setTrip((current) => ({ ...current, returnDate: event.target.value }))} /></label>
                <label>Departure airport (IATA)<input value={trip.originIata} maxLength="3" placeholder="SIN" onChange={(event) => setTrip((current) => ({ ...current, originIata: event.target.value.toUpperCase() }))} /></label>
              </div>
            </article>}

            <article className="deal-card">
              <p className="teleport-eyebrow">Traveller Information</p>
              <h2>Who is travelling?</h2>
              <p className="deal-card__intro">Select exactly {requiredTravellerCount} saved traveller{requiredTravellerCount === 1 ? "" : "s"}. Details come from Traveller Information, just like in the app.</p>
              {!localStorage.getItem("teleport_token") ? (
                <div className="deal-traveller-state"><strong>Sign in to select Traveller Information.</strong><button onClick={() => navigate("/login", { state: { from: location.pathname } })}>Sign in</button></div>
              ) : travellersLoading ? (
                <div className="deal-traveller-state">Loading Traveller Information…</div>
              ) : travellersError ? (
                <div className="deal-traveller-state"><strong>{travellersError}</strong><button onClick={() => window.location.reload()}>Try again</button></div>
              ) : savedTravellers.length === 0 ? (
                <div className="deal-traveller-state"><strong>No saved travellers yet.</strong><span>Add Traveller Information before continuing.</span><button onClick={() => navigate("/my-profile", { state: { activeTab: "travellers" } })}>Manage Traveller Information</button></div>
              ) : (
                <div className="deal-saved-travellers">
                  {savedTravellers.map((traveller) => {
                    const id = travellerId(traveller);
                    const selected = selectedTravellerIds.includes(id);
                    const disabled = !selected && selectedTravellerIds.length >= requiredTravellerCount;
                    return <button type="button" key={id} className={selected ? "is-selected" : ""} disabled={disabled} onClick={() => toggleTraveller(traveller)}>
                      <span className="deal-saved-travellers__avatar">{String(traveller.firstName || "T").charAt(0).toUpperCase()}</span>
                      <span><strong>{traveller.firstName} {traveller.lastName}</strong><small>{formatTravellerDob(traveller.dob)} · {traveller.documentType || "Passport"} {traveller.documentNumber || "not provided"}</small></span>
                      <span className="deal-saved-travellers__check">{selected && <Check />}</span>
                    </button>;
                  })}
                  <button type="button" className="deal-manage-travellers" onClick={() => navigate("/my-profile", { state: { activeTab: "travellers" } })}>Manage Traveller Information</button>
                </div>
              )}
            </article>

            {deal.services?.filter((service) => service.required && service.serviceType === "airportLounge" && service.loungeSelectionMode === "countryPool").map((service) => <article className="deal-card" key={service.key}><p className="teleport-eyebrow">Airport lounge</p><h2>Choose your included lounge.</h2><button className="deal-secondary-button" onClick={() => loadLounges(service)}>View available lounges</button><div className="deal-lounges">{(loungeOptions[service.key] || []).map((option) => { const selected = selections[service.key]?.loungeId === option.id; return <button className={selected ? "is-selected" : ""} key={option.id} onClick={() => setSelections((current) => ({ ...current, [service.key]: { ...current[service.key], loungeId: option.id, loungeName: option.loungeName, airport: option.airport, visitDate: trip.departureDate, acceptedCurrency: option.acceptedCurrency, acceptedSellingAmount: option.acceptedSellingAmount } }))}><img src={option.imageUrl || deal.heroImageUrl} alt="" /><span><strong>{option.loungeName}</strong><small>{[option.airport,option.terminal].filter(Boolean).join(" · ")}</small></span>{selected && <Check />}</button>; })}</div></article>)}
          </section>

          <aside className="deal-purchase-card">
            <p className="teleport-eyebrow">Your Deal</p>
            <div className="deal-purchase-card__price"><span>From</span><strong>{deal.currency} {pricing.deal.toFixed(2)}</strong>{pricing.original > pricing.deal && <del>{deal.currency} {pricing.original.toFixed(2)}</del>}</div>
            <div className="deal-purchase-card__quantity">
              <strong>Traveller quantity</strong>
              <span>{requiredTravellerCount} traveller{requiredTravellerCount === 1 ? "" : "s"}</span>
              <div className="deal-party">{[["adults","Adults"],["children","Children"],["infants","Infants"]].map(([key,label]) => <div key={key}><span><strong>{label}</strong><small>{key === "adults" ? "Age 13+" : key === "children" ? "Age 2–12" : "Under 2"}</small></span><div><button type="button" onClick={() => updateParty(key,-1)} aria-label={`Remove ${label}`}><Minus /></button><strong>{party[key]}</strong><button type="button" onClick={() => updateParty(key,1)} aria-label={`Add ${label}`}><Plus /></button></div></div>)}</div>
            </div>
            <ul><li><Check /> {deal.services?.length || 0} included services</li><li><Check /> Live availability checked before cart</li><li><Check /> Secure combined checkout</li></ul>
            <button onClick={addToCart} disabled={adding}><ShoppingBag /> {adding ? "Preparing your deal…" : "Add deal to cart"} <ChevronRight /></button>
            <p>Final availability and any additional live cost are shown before payment.</p>
          </aside>
        </div>
      </main>
      <Footer />
    </>
  );
}
